import { useState } from 'preact/hooks';
import { Calculator, Lightning, TrendUp, TrendDown, CurrencyDollar, Info, WifiSlash, ChartBar, Eye, EyeSlash } from '@phosphor-icons/react';
import type { PoolResponse } from '../../services/api.js';
import { EnergyHistory } from './EnergyHistory.js';

interface Props {
  pool: PoolResponse | null;
  totalPower: number;
  electricityRate: number;
  activeFarmsCount: number;
  usdRub: number;
}

export function ProfitCalculator({ pool, totalPower, electricityRate, activeFarmsCount, usdRub }: Props) {
  const [showHistory, setShowHistory] = useState(false);
  // Глазик: по умолчанию все суммы скрыты за звёздочками
  const [revealed, setRevealed] = useState(false);
  const m = (v: string) => (revealed ? v : '✱✱✱');
  const coinPrice = pool ? parseFloat(pool.pool.coinPriceUsd) : 0;

  // Daily XEL from pool sumrewards[2] = last 24h (in satoshis)
  const dailyRewardSat = pool?.miner.sumrewards[2]?.reward ?? 0;
  const dailyXel = dailyRewardSat / 1e8;
  const dailyRevenueUsd = dailyXel * coinPrice;
  const monthlyXel = dailyXel * 30;
  const monthlyRevenueUsd = dailyRevenueUsd * 30;

  // Electricity cost
  const dailyKwh = (totalPower * 24) / 1000;
  const dailyElecRub = dailyKwh * electricityRate;
  const dailyElecUsd = dailyElecRub / usdRub;
  const monthlyElecRub = dailyElecRub * 30;
  const monthlyElecUsd = dailyElecUsd * 30;

  // Net profit
  const dailyProfit = dailyRevenueUsd - dailyElecUsd;
  const monthlyProfit = monthlyRevenueUsd - monthlyElecUsd;

  // Breakeven: at what XEL price does revenue = electricity cost
  const breakevenPrice = dailyXel > 0 ? dailyElecUsd / dailyXel : 0;

  const color = (v: number) => v >= 0 ? 'profit-positive' : 'profit-negative';
  const sign = (v: number) => v >= 0 ? '+' : '-';

  return (
    <div class="data-card">
      <div class="data-card-header" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Calculator size={16} />
          <span>Калькулятор прибыли</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <button
            class="btn btn-secondary"
            style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }}
            onClick={() => setRevealed((v) => !v)}
            title={revealed ? 'Скрыть суммы' : 'Показать суммы'}
          >
            {revealed ? <EyeSlash size={12} /> : <Eye size={12} />}
          </button>
          <button class="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 'var(--text-xs)' }} onClick={() => setShowHistory(true)}>
            <ChartBar size={12} />
            <span>История</span>
          </button>
        </div>
      </div>

      {showHistory && <EnergyHistory onClose={() => setShowHistory(false)} />}
      <div class="data-card-body">
        {activeFarmsCount === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', background: 'rgba(245,54,92,0.1)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', color: 'var(--red)' }}>
            <WifiSlash size={14} />
            <span>Все фермы offline — расчёт на основе последних данных пула</span>
          </div>
        )}
        <div class="profit-grid">
          <Metric label="Доход / день" value={m(`${dailyXel.toFixed(2)} XEL`)} sub={revealed ? `$${dailyRevenueUsd.toFixed(2)}/${(dailyRevenueUsd * usdRub).toFixed(0)}₽` : undefined} />
          <Metric label="Доход / месяц" value={m(`${monthlyXel.toFixed(2)} XEL`)} sub={revealed ? `$${monthlyRevenueUsd.toFixed(2)}/${(monthlyRevenueUsd * usdRub).toFixed(0)}₽` : undefined} />
          <Metric label="Электричество / день" value={m(`${dailyElecRub.toFixed(2)} ₽`)} sub={revealed ? `$${dailyElecUsd.toFixed(2)}` : undefined} />
          <Metric label="Электричество / месяц" value={m(`${monthlyElecRub.toFixed(0)} ₽`)} sub={revealed ? `$${monthlyElecUsd.toFixed(2)}` : undefined} />
          <Metric
            label={dailyProfit >= 0 ? 'Прибыль / день' : 'Убыток / день'}
            value={m(`${sign(dailyProfit)}$${Math.abs(dailyProfit).toFixed(2)}/${sign(dailyProfit)}${Math.abs(dailyProfit * usdRub).toFixed(0)}₽`)}
            valueClass={revealed ? color(dailyProfit) : undefined}
            breakdown={revealed ? [
              `Доход: ${dailyXel.toFixed(2)} XEL × $${coinPrice.toFixed(2)} = $${dailyRevenueUsd.toFixed(2)} (${(dailyRevenueUsd * usdRub).toFixed(0)} ₽)`,
              `Электричество: ${dailyKwh.toFixed(1)} кВтч × ${electricityRate} ₽/кВтч = ${dailyElecRub.toFixed(2)} ₽ ($${dailyElecUsd.toFixed(2)})`,
              `Итого: $${dailyRevenueUsd.toFixed(2)} − $${dailyElecUsd.toFixed(2)} = ${sign(dailyProfit)}$${Math.abs(dailyProfit).toFixed(2)} (${sign(dailyProfit)}${Math.abs(dailyProfit * usdRub).toFixed(0)} ₽)`,
            ] : undefined}
          />
          <Metric
            label={monthlyProfit >= 0 ? 'Прибыль / месяц' : 'Убыток / месяц'}
            value={m(`${sign(monthlyProfit)}$${Math.abs(monthlyProfit).toFixed(2)}/${sign(monthlyProfit)}${Math.abs(monthlyProfit * usdRub).toFixed(0)}₽`)}
            valueClass={revealed ? color(monthlyProfit) : undefined}
            breakdown={revealed ? [
              `Доход: ${monthlyXel.toFixed(2)} XEL × $${coinPrice.toFixed(2)} = $${monthlyRevenueUsd.toFixed(2)} (${(monthlyRevenueUsd * usdRub).toFixed(0)} ₽)`,
              `Электричество: ${(dailyKwh * 30).toFixed(0)} кВтч × ${electricityRate} ₽/кВтч = ${monthlyElecRub.toFixed(0)} ₽ ($${monthlyElecUsd.toFixed(2)})`,
              `Итого: $${monthlyRevenueUsd.toFixed(2)} − $${monthlyElecUsd.toFixed(2)} = ${sign(monthlyProfit)}$${Math.abs(monthlyProfit).toFixed(2)} (${sign(monthlyProfit)}${Math.abs(monthlyProfit * usdRub).toFixed(0)} ₽)`,
            ] : undefined}
          />
          <Metric
            label="Безубыточность XEL"
            value={m(`$${breakevenPrice.toFixed(2)}`)}
            valueClass={revealed ? (breakevenPrice > coinPrice ? 'profit-negative' : 'profit-positive') : undefined}
            sub={revealed ? (breakevenPrice > coinPrice ? 'Выше текущей цены!' : 'Ниже текущей цены') : undefined}
          />
          <Metric label="Потребление" value={`${dailyKwh.toFixed(1)} кВтч/день`} sub={`${totalPower} Вт`} />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, sub, valueClass, breakdown }: { label: string; value: string; sub?: string; valueClass?: string; breakdown?: string[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div class={`profit-metric${breakdown ? ' has-tooltip' : ''}`}>
      <div class={`profit-metric-value${valueClass ? ` ${valueClass}` : ''}`}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <div class="profit-metric-label">{label}</div>
        {breakdown && (
          <button
            class="breakdown-toggle"
            onClick={() => setOpen((v) => !v)}
            title="Расшифровка расчёта"
          >
            <Info size={14} />
          </button>
        )}
      </div>
      {sub && <div class="profit-metric-label" style={{ opacity: 0.7 }}>{sub}</div>}
      {breakdown && open && (
        <div class="profit-breakdown">
          {breakdown.map((line, i) => (
            <div key={i} class="profit-breakdown-line">{line}</div>
          ))}
        </div>
      )}
    </div>
  );
}
